import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import type { BimWorkOrderChangePriceData } from 'gql/graphql';
import { getCheckedIdsFromObjValues } from 'utils';

import type { CellClassParams, ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { BimWorkOrderRowDetailsType } from '../../../WorkOrder/types';
import { BimWorkOrderChangeMenuRenderer } from '../components/cellRenderers';
import type { TDetailsData } from '../components/cellRenderers';

export const useWorkOrderChangeMenuColumn = ({
    workOrderChangeStatus,
    refreshTable,
}: {
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
    refreshTable: () => void;
}) => {
    const [checkedPriceDetailsRow, setCheckedPriceDetailsRow] = useState<TDetailsData | null>(null);

    const [checkedRows, setCheckedRows] = useState<Record<number, BimWorkOrderChangePriceData | null>>({});

    const checkRowHandler = useCallback((id, state) => {
        setCheckedRows((prevState) => ({ ...prevState, [id]: state }));
    }, []);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const canDelete = useMemo(
        () => workOrderChangeStatus === WorkOrderChangeStatusEnum.OnFormation,
        [workOrderChangeStatus],
    );

    const openDetailsHandler = useCallback(({ rowType, detailsType, data }: TDetailsData) => {
        if (data && detailsType) {
            setCheckedPriceDetailsRow((prevState) => {
                if (prevState) {
                    const { prevCheckedPrice, ...currentCheckedPrice } = prevState ?? {};

                    if (currentCheckedPrice) {
                        return {
                            prevCheckedPrice: currentCheckedPrice,
                            rowType,
                            detailsType,
                            data,
                        };
                    }
                }

                return {
                    rowType,
                    detailsType,
                    data,
                };
            });
        }
    }, []);

    const handlerCloseDetails = useCallback(() => {
        setCheckedPriceDetailsRow((prevState) => {
            if (prevState?.prevCheckedPrice) {
                return {
                    ...prevState.prevCheckedPrice,
                    prevCheckedPrice: null,
                };
            }

            return null;
        });
    }, []);

    const isMenuVisible = useMemo(
        () =>
            !(
                checkedPriceDetailsRow?.detailsType &&
                [BimWorkOrderRowDetailsType.Revision, BimWorkOrderRowDetailsType.Update].includes(
                    checkedPriceDetailsRow?.detailsType,
                )
            ),
        [checkedPriceDetailsRow?.detailsType],
    );

    useEffect(() => {
        if (workOrderChangeStatus !== WorkOrderChangeStatusEnum.OnFormation) {
            setCheckedRows({});
        }
    }, [workOrderChangeStatus]);

    const menuColumn = useMemo<ColDef<BimWorkOrderChangePriceData>>(
        () => ({
            headerName: '',
            field: 'menu' as any,
            width: 100,
            resizable: false,
            pinned: 'left',
            lockPosition: 'left',
            cellRenderer: BimWorkOrderChangeMenuRenderer,
            cellRendererParams: {
                checkRowHandler,
                checkedRows,
                isActionDisabled: !canDelete,
                openDetailsHandler,
                isMenuVisible,
                refreshTable,
                workOrderChangeStatus,
            },
            cellClassRules: {
                bottomPinnedMenuCell: (params: CellClassParams) => params.node.rowPinned === 'bottom',
                centeredAgGridCell: (params: CellClassParams) => !params.node.rowPinned,
            },
        }),
        [
            canDelete,
            checkRowHandler,
            checkedRows,
            isMenuVisible,
            openDetailsHandler,
            refreshTable,
            workOrderChangeStatus,
        ],
    );

    const preparedToDelete = useMemo(
        () =>
            getCheckedIdsFromObjValues(['workOrderChangePriceId', 'workOrderPriceId'])(checkedRows) as {
                workOrderChangePriceId: number | undefined;
                workOrderPriceId: number | undefined;
            }[],
        [checkedRows],
    );

    return {
        menuColumn,
        checkedRows: preparedToDelete,
        resetCheckedRows,
        checkedDetailsRow: checkedPriceDetailsRow?.data,
        detailsType: checkedPriceDetailsRow?.detailsType,
        detailsRowType: checkedPriceDetailsRow?.rowType,
        handlerCloseDetails,
    };
};
